/**
 * @file src/api/device.api.js
 * @description All device-related API calls.
 *
 * Backend company/branch context:
 *  - Backend derives companyId from selected-company cookie.
 *  - Device creation must include branchId.
 *  - Device creation must NOT include companyId.
 *  - Device list may optionally filter by branchId.
 */

import { apiClient } from "lib/axios";

/**
 * Register a new EGS device with ZATCA.
 *
 * @param {object} body - Device registration payload, including branchId
 * @returns {Promise<object>} Registered device data
 */
export const registerDevice = async (body) => {
  const { companyId, ...cleanBody } = body;

  const { data } = await apiClient.post("/devices/", cleanBody);
  return data;
};

/**
 * Fetch registered devices for the selected company.
 * Optionally filter by branchId.
 *
 * @param {{ branchId?: string }} params
 * @returns {Promise<object>} Device list response
 */
export const getDevices = async (params = {}) => {
  const { data } = await apiClient.get("/devices/", { params });
  return data;
};

/**
 * Submit Compliance CSR for a device to obtain a Compliance CSID from ZATCA.
 *
 * @param {{ deviceId: string, otp: string }} params
 * @returns {Promise<object>} Compliance CSID response
 */
export const generateComplianceCSR = async ({ deviceId, otp }) => {
  const { data } = await apiClient.post(`/devices/${deviceId}/submit-csr`, {
    otp,
  });
  return data;
};

/**
 * Generate Production CSID for a device.
 *
 * @param {string} deviceId
 * @returns {Promise<object>} Production CSID response
 */
export const generateProductionCSID = async (deviceId) => {
  const { data } = await apiClient.post(
    `/devices/${deviceId}/production-csid`,
    {},
  );
  return data;
};
